// OutreachChatContext — exposes the outreach chat shell's resume entry
// point to widgets rendered inside the assistant message tree (H.5a.2).
//
// H.5a.2 Task 12: migrated from manual SSE-drain + AI-SDK setMessages to
// the shell hook's `resumeFrom` — which speaks AG-UI and appends directly
// to the shared `messages` state without any AI-SDK types.
//
// Mirror of apps/web-admin/src/lib/admin-chat-context.tsx (AdminChatProvider):
// the founder-decision widget calls useOutreachChatContext() UNCONDITIONALLY
// and THROWS without a provider. The chat shell wraps its message tree in
// <OutreachChatProvider> so every confirmation widget can call
// resumeFromDecision(...) without prop-drilling.
//
// Click flow: founder clicks an option (founder-decision) → resumeFromDecision
// POSTs to /outreach-agent/decision → outreach-agent runs the confirmed
// tool and resumes runWithSDK → the AG-UI SSE response is consumed by the
// shell hook and appended as a fresh assistant bubble in this chat.
//
// Includes the outreach `scope` (a prospect focus selector) which the
// decision endpoint needs so it can forward context to the agent — unlike
// the admin context which has no scope concept.

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { SendOptions } from '@rmint/shell';
import type { OutreachScope } from '../components/outreach/ScopeChip';

export interface OutreachChatCtx {
  /**
   * POST the founder's click to /outreach-agent/decision + drain the
   * resumed AG-UI SSE response into the chat stream as a new assistant
   * bubble. Throws on a pre-stream POST failure (the audit event did NOT
   * land).
   */
  resumeFromDecision(payload: {
    sessionId: string;
    tool_use_id: string;
    action: string;
    args?: Record<string, unknown>;
  }): Promise<void>;
}

export const OutreachChatContext = createContext<OutreachChatCtx | null>(null);

/**
 * Throws when used outside an OutreachChatProvider — the chat shell is the
 * only legitimate provider; absence means a widget was mounted outside the
 * chat tree (or the provider was forgotten in tests).
 */
export function useOutreachChatContext(): OutreachChatCtx {
  const ctx = useContext(OutreachChatContext);
  if (!ctx) {
    throw new Error(
      '[outreach-chat-context] OutreachChatContext not provided — wrap in OutreachChatProvider',
    );
  }
  return ctx;
}

/**
 * Provider — delegates to the shell hook's `resumeFrom` so the resumed
 * AG-UI SSE appends to the same messages state. Takes `resumeFrom` from
 * the Chat component (the useChat result) so every confirmation widget
 * can trigger it without prop-drilling.
 *
 * Includes `scope` so the decision endpoint can forward prospect context
 * to the agent (outreach-specific — admin context has no scope).
 */
export function OutreachChatProvider({
  resumeFrom,
  scope,
  children,
}: {
  resumeFrom: (url: string, body: Record<string, unknown>, opts?: SendOptions) => Promise<void>;
  scope: OutreachScope;
  children: ReactNode;
}) {
  const value = useMemo<OutreachChatCtx>(
    () => ({
      resumeFromDecision: async (payload) => {
        await resumeFrom('/outreach-agent/decision', { ...payload, scope });
      },
    }),
    [resumeFrom, scope],
  );

  return (
    <OutreachChatContext.Provider value={value}>
      {children}
    </OutreachChatContext.Provider>
  );
}
